import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Notifications from "./Notifications";
import { logoutUser } from "../modules/UserManagement/services/authService";

function Header({ productName = "LeadManager", shopName = "ShopName", notifications = [], user }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  const getInitials = (name) => {
    if (!name) return "U";
    const nameParts = name.split(" ");
    return nameParts.length >= 2
      ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
      : nameParts[0][0].toUpperCase();
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="bg-gradient-to-r from-blue-700 via-blue-600 to-blue-500 shadow-lg fixed top-0 left-0 w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          {/* Hamburger Menu (Mobile) */}
          <button className="text-white md:hidden" onClick={toggleMenu}>
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          {/* Product Name */}
          <h1 className="text-white text-lg sm:text-2xl font-extrabold">
            {productName}
          </h1>

          {/* Shop Name (Full on Desktop, Shortened on Mobile) */}
          <span className="text-gray-200 text-xs sm:text-sm font-medium hidden sm:inline">
            | {shopName}
          </span>
          <span className="text-gray-200 text-xs font-medium sm:hidden">
            | {shopName.split(" ")[0]} {shopName.split(" ")[1]} {/* Show only first two words */}
          </span>
        </div>

        {/* Center Section (Search Bar) */}
        <div className="hidden sm:flex flex-1 justify-center">
          <input
            type="text"
            className="w-full max-w-xs sm:max-w-sm lg:max-w-md px-4 py-2 text-sm rounded-md bg-gray-100 focus:outline-none focus:ring focus:ring-blue-400"
            placeholder="Search Leads, Opportunities..."
          />
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Search Icon (Mobile) */}
          <button className="sm:hidden text-white">🔍</button>

          {/* Notifications */}
          <Notifications notifications={notifications} />

          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button onClick={toggleDropdown} className="flex items-center gap-2 text-white focus:outline-none">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center shadow-md">
                <span className="text-blue-700 font-bold">{getInitials(user?.fullName)}</span>
              </div>
              <span className="hidden sm:block text-sm font-medium">{user?.fullName}</span>
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2">
                <Link to="/profile" className="block px-4 py-2 text-gray-800 hover:bg-gray-100 text-sm">
                  Profile
                </Link>
                <Link to="/settings" className="block px-4 py-2 text-gray-800 hover:bg-gray-100 text-sm">
                  Settings
                </Link>
                <button
                  onClick={logoutUser}
                  className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 text-sm"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu (Slide-down) */}
      {menuOpen && (
        <div className="md:hidden bg-white shadow-lg py-2">
          <Link to="/" className="block px-4 py-2 text-gray-800 hover:bg-gray-100">Dashboard</Link>
          <Link to="/leads" className="block px-4 py-2 text-gray-800 hover:bg-gray-100">Leads</Link>
          <Link to="/opportunities" className="block px-4 py-2 text-gray-800 hover:bg-gray-100">Opportunities</Link>
        </div>
      )}
    </header>
  );
}

export default Header;